import { useRef, useState, type ReactNode } from 'react';
import { useTransition, config, animated } from '@react-spring/web';
import clsx from 'clsx';
import { FocusScope, useOverlay, usePreventScroll } from 'react-aria';
import { useExperiment } from '@hungryroot/experiments';
import { Dimension } from '@hungryroot/ramen';
import { OverlayTypes, type OverlayState } from '../../../../hooks/useOverlays';

import styles from './MobileOverlay.module.css';

interface Props {
  overlayDef: {
    overlay: ReactNode;
    overlayState: OverlayState;
  };
  children: ReactNode;
  onDismiss: () => void;
}

const SPRING_CONFIG = {
  ...config.stiff,
  bounce: 0,
};

export function MobileOverlay(props: Props) {
  const { overlayDef, children, onDismiss } = props;
  const { overlay, overlayState } = overlayDef;
  // remember the "last" overlay,
  // because when this gets dismissed,
  // we still need the overlay to be rendered in the DOM
  // for a smooth exit animation
  const overlayRef = useRef(overlay);
  if (overlayRef.current !== overlay && !!overlay) {
    overlayRef.current = overlay;
  }

  // This is the ref
  // Where the overlay will be rendered to
  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasOverlay = overlayState.type !== OverlayTypes.None;
  const [renderStatic, setRenderStatic] = useState(!hasOverlay);
  if (hasOverlay === true && renderStatic === true) {
    setRenderStatic(false);
  }

  const overlayTransition = useTransition(hasOverlay, {
    config: SPRING_CONFIG,
    from: {
      opacity: 0,
    },
    enter: {
      delay: 600,
      opacity: 1,
    },
    leave: {
      delay: 0,
      opacity: 0,
    },
  });

  const backgroundTransition = useTransition(hasOverlay, {
    config: SPRING_CONFIG,
    from: {
      opacity: 0,
    },
    enter: {
      delay: 600,
      opacity: 1,
    },
    leave: {
      delay: 0,
      opacity: 0,
    },
    onRest() {
      if (hasOverlay === false) {
        setRenderStatic(true);
      }
    },
  });

  const isStatic = renderStatic === true ? styles.sticky : styles.active;

  usePreventScroll({
    isDisabled: renderStatic === true,
  });

  const { overlayProps } = useOverlay(
    {
      isOpen: true,
      isDismissable: true,
      onClose: onDismiss,
    },
    containerRef
  );

  if (renderStatic === true) {
    return (
      <div data-testid="mobile-overlay" className={isStatic}>
        {children}
      </div>
    );
  }

  return (
    <FocusScope contain autoFocus>
      <div
        {...overlayProps}
        ref={containerRef}
        data-testid="mobile-overlay-portal-container"
        className={isStatic}
      >
        {backgroundTransition(({ opacity }, active) => {
          if (active === false) {
            return null;
          }

          return (
            <animated.div
              data-testid="mobile-overlay-background"
              style={{ opacity }}
              className={styles.background}
            />
          );
        })}
        {overlayTransition(({ opacity }, active) => {
          if (active === false) {
            return null;
          }

          return (
            <animated.div
              style={{ opacity }}
              className={styles['overlay-contents']}
              onClick={onDismiss}
            >
              {overlayRef.current}
            </animated.div>
          );
        })}
        {children}
      </div>
    </FocusScope>
  );
}

export const MobileOverlayCartPreview = (props: {
  cartPreviewVisible: boolean;
  cartPreview: ReactNode;
  cartSummary: ReactNode;
}) => {
  const { cartSummary, cartPreviewVisible, cartPreview } = props;
  const [renderStaticCartPreview, setRenderStaticCartPreview] =
    useState(!cartPreviewVisible);
  if (cartPreviewVisible === true && renderStaticCartPreview === true) {
    setRenderStaticCartPreview(false);
  }

  const discoveryHomeVariant = useExperiment(188);

  const previewTransition = useTransition(cartPreviewVisible, {
    config: SPRING_CONFIG,
    from: {
      transform: 'translate3d(0, 161px, 0)',
    },
    enter: {
      delay: 600,
      transform: 'translate3d(0, 0px, 0)',
    },
    leave: {
      delay: 0,
      transform: 'translate3d(0, 161px, 0)',
    },
    onRest() {
      if (cartPreviewVisible === false) {
        setRenderStaticCartPreview(true);
      }
    },
  });

  if (discoveryHomeVariant === undefined) {
    return null;
  }

  if (renderStaticCartPreview === true) {
    if (discoveryHomeVariant > 0) {
      return (
        <Dimension as="div" level="four">
          {cartSummary}
        </Dimension>
      );
    } else {
      return (
        <div className={styles['cart-summary-container']}>{cartSummary}</div>
      );
    }
  }

  return (
    <>
      {previewTransition(({ transform }, active) => {
        if (active === false) {
          return null;
        }

        return (
          <animated.div
            style={{ transform }}
            className={clsx({
              [styles['cart-summary-container']]: discoveryHomeVariant < 1,
            })}
          >
            {cartSummary}
            {cartPreview}
          </animated.div>
        );
      })}
    </>
  );
};
